import { Alert, Box, Button, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import type { BillingLedger } from '../../../contexts/BillingContext';
import type { PricingSettings } from '../../../shared/constants/pricing';

interface CreditsAndUsageProps {
  ledger: BillingLedger | null;
  pricingSettings: PricingSettings;
  billingCurrency: string;
  totalUnbilledUsageCost: number;
}

export const CreditsAndUsage: React.FC<CreditsAndUsageProps> = ({
  ledger,
  pricingSettings,
  billingCurrency,
  totalUnbilledUsageCost,
}) => {
  const theme = useTheme();

  return (
    <Paper sx={{ p: 3, backgroundColor: theme.palette.greyLight, height: '100%' }}>
      <Typography variant="h5" gutterBottom>
        Credits and Usage
      </Typography>

      <Box sx={{ my: 3, p: 2, backgroundColor: 'background.paper', borderRadius: 1 }}>
        <Typography variant="subtitle1" color="text.secondary">
          Current Balance
        </Typography>
        <Typography
          variant="h3"
          color={
            ledger?.balance === 0
              ? theme.palette.mode === 'dark'
                ? 'error.light'
                : 'error.main'
              : theme.palette.mode === 'dark'
                ? 'primary.light'
                : 'primary.main'
          }
        >
          $
          {((ledger?.balance || 0) * (pricingSettings.cost_per_credit_in_base || 0)).toLocaleString(
            undefined,
            {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }
          )}{' '}
          {billingCurrency}
        </Typography>
      </Box>

      <Box sx={{ my: 3, p: 2, backgroundColor: 'background.paper', borderRadius: 1 }}>
        <Typography variant="subtitle1" color="text.secondary">
          Accumulated Usage (Unbilled)
        </Typography>
        <Typography variant="h3">
          $
          {totalUnbilledUsageCost.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}{' '}
          {billingCurrency}
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mt: 2 }}>
        Automated top-ups are currently disabled. Please contact your administrator to purchase
        additional credits or request an invoice.
      </Alert>

      <Button
        variant="contained"
        color="primary"
        sx={{ mt: 3 }}
        onClick={() => (window.location.href = 'mailto:billing@bidlogiq.ai')}
      >
        Contact Billing Support
      </Button>
    </Paper>
  );
};
